<template>
  <v-container class="relative" style="max-width: 960px">
    <div v-if="loading">
      <app-spinner />
    </div>

    <div v-else class="ma-auto">
      <TaskListModal
        :modal-visible="showTaskListModal"
        :business-brand="business.brand"
        :proof-types="offer.tasks"
        :completed-proof-types="proofs"
        @task-list-modal-close="$router.go(-1)"
      />

      <v-dialog v-model="webStartDialog" transition="dialog-bottom-transition">
        <v-card class="rounded-corners" data-cy="howToOrderModal">
          <v-container>
            <v-row align="center" justify="center">
              <v-col class="pb-0" cols="11" align="center" justify="center"><p class="big-title">How to order</p></v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col class="pb-0" cols="11" align="center" justify="center">
                <p class="dialog-instruction">
                  Visit {{ business.brand }} website by tapping the link below. Then return here when you are ready to checkout to get your promo code.
                </p>
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col class="pb-0" cols="11" align="center" justify="center">
                <div v-if="offer.offer_url" class="body-2 font-weight-light primary--text">
                  <v-icon dense small color="primary" class="mt-n1 pr-1">mdi-open-in-new</v-icon>
                  <a :href="offer.offer_url" target="_blank" class="text-decoration-underline">{{ offer.offer_url }}</a>
                </div>
              </v-col>
            </v-row>
            <v-row align="center" justify="center">
              <v-col class="pb-0" cols="10" align="center" justify="center">
                <v-btn color="primary" block rounded data-cy="readyToPurchaseButton" @click="redeemWebOffer">YES, I'M READY TO PURCHASE</v-btn>
              </v-col>
            </v-row>
            <v-row align="center" justify="center" class="mb-5">
              <v-col class="pb-0" cols="10" align="center" justify="center">
                <v-btn block outlined color="primary" rounded dark @click="webStartDialog = false">NOT YET</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <v-dialog v-model="codeDialog" transition="dialog-bottom-transition">
        <v-card class="rounded-corners" data-cy="promoCodeModal">
          <v-container>
            <v-row align="center" justify="center">
              <v-col class="pb-0" cols="11" align="center" justify="center"><p class="big-title">YOUR PROMO CODE</p></v-col>
            </v-row>
            <v-row v-if="hasWebCode" align="center" justify="center">
              <v-col class="pb-0" cols="11" align="center" justify="center">
                <input id="promoCode" ref="promoCode" class="rounded-red promo-code" :value="webCode" readonly data-cy="promoCode" />
              </v-col>
            </v-row>
            <v-row align="center" justify="center" class="mt-5 mb-5">
              <v-col class="pb-0" cols="10" align="center" justify="center">
                <v-btn block outlined color="primary" rounded dark data-cy="copyCodeButton" @click="copyCode">COPY CODE</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <ScheduleAppointmentModal
        v-if="showScheduleAppointmentModal"
        :loading="!isVoucherRedeemed"
        :phone-number="offer.offer_phone || (address && address.address_phone)"
        :schedule-url="offer.offer_url"
        :brand="business.brand"
        :offer-description="offer.offer_summary"
        :min-days-between-offer="offer.min_days_between_offer"
        @schedule-appointment-modal-close="showScheduleAppointmentModal = false"
        @schedule-appointment-modal-close-nadda="showScheduleAppointmentModal = false"
      />

      <v-row>
        <v-col cols="12" justify="center" align="center" class="mt-4">
          <v-img v-if="business.logo_url" :src="business.logo_url" max-width="90%" max-height="55" contain :eager="true" class="mb-4" />
          <div v-else>
            <p class="brand-name-logo-replacement">{{ business.brand }}</p>
          </div>
        </v-col>
      </v-row>

      <div v-if="!isVoucherComplete">
        <v-row v-if="offer.offer_phone || (address && address.address_phone)">
          <v-col align="center" justify="center" class="body-2 font-weight-light mt-n3">
            <v-icon dense small color="primary" class="mt-n1">mdi-phone</v-icon>
            {{ offer.offer_phone || address.address_phone }}
          </v-col>
        </v-row>

        <v-row v-if="address">
          <v-col align="center" justify="center" class="body-2 font-weight-light mt-n3">
            <v-icon dense small color="primary" class="mt-n1">mdi-map-marker</v-icon>
            {{ address.address }} {{ address.city }}, {{ address.zipcode }}
          </v-col>
        </v-row>
        <v-row v-if="isEventVoucher">
          <v-col align="center" justify="center" class="body-2 font-weight-light mt-n3">
            <v-icon dense small color="primary" class="mt-n1">mdi-calendar</v-icon>
            <span v-if="daysToEvent < 0"> {{ getEventDateFull }} days ago</span>
            <span v-else-if="daysToEvent === 0"> <strong class="red--text font-weight-black"> TODAY</strong> {{ getEventDateFull }}</span>
            <span v-else>Starts in {{ daysToEvent }} {{ 'day' | pluralize(daysToEvent) }} - {{ getEventDateFull }}</span>
          </v-col>
        </v-row>

        <v-row v-if="offer.offer_url">
          <v-col align="center" justify="center" class="body-2 font-weight-light primary--text mt-n3">
            <v-icon dense small color="primary" class="mt-n1 pr-1">mdi-open-in-new</v-icon>
            <a :href="offer.offer_url" target="_blank" class="text-decoration-underline">{{ offer.offer_url }}</a>
          </v-col>
        </v-row>

        <v-row v-if="!isVoucherRedeemed" align="center" justify="center" class="mt-n4 mb-4">
          <v-col cols="11" align="center">
            <h3 class="header mt-4 font-weight-light">Hello, let's walk you through this {{ business.brand }} offer</h3>
          </v-col>
        </v-row>

        <v-row v-else align="center" justify="center" class="mb-4">
          <v-col cols="11" align="center">
            <v-img :src="require('@/assets/instructions_03.svg')" contain height="125" />
            <h3 class="header font-weight-light mt-4 mx-8">Yay! Now lets choose what action you'll be doing</h3>
          </v-col>
        </v-row>
      </div>

      <div v-else>
        <v-row>
          <v-col align="center" justify="center">
            <div class="offer-complete-circle">
              <v-icon x-large color="#009200">mdi-check-bold</v-icon>
            </div>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col align="center" justify="center">
            <h2 class="header font-weight-light mx-8">This offer has been successfully completed</h2>
          </v-col>
        </v-row>
      </div>

      <v-row v-if="isWebVoucher" class="action-blocks">
        <v-col cols="12">
          <OfferActionBlock
            header="Proceed to Website & Order"
            description="We'll provide the promo code when you're ready to order"
            action-text="Start"
            :action-number="1"
            :action-complete="isVoucherRedeemed"
            :action-sub-text="`Expires in ${$options.filters.timeUntil(voucher.date_expires)}`"
            :action-repeatble="true"
            @offer-action-block-link-clicked="showWebStartDialog()"
          />

          <OfferActionBlock
            header="Make Post or Review"
            description="Create your social post or review & upload a screenshot."
            action-text="Start"
            :action-number="2"
            :action-complete="proofs.length > 0"
            :action-disabled="!isVoucherRedeemed"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />

          <OfferActionBlock
            v-if="proofs.length > 0"
            header="Add a Second Post or Review"
            :description="`Love ${business.brand}? Add another post or review & upload a screenshot.`"
            action-text="Start"
            :action-number="3"
            :action-complete="proofs.length > 1"
            :action-sub-text="offer.number_tasks_required > 1 ? '' : 'Optional'"
            :action-disabled="!isVoucherRedeemed"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />
        </v-col>
      </v-row>

      <v-row v-if="isCallVoucher" class="action-blocks">
        <v-col cols="12">
          <OfferActionBlock
            header="Schedule Appointment"
            description="Click here set your appointment."
            action-text="Start"
            :action-number="1"
            :action-repeatble="true"
            :action-complete="isAppointmentScheduled"
            :action-sub-text="`Schedule within 5 days`"
            @offer-action-block-link-clicked="scheduleVoucher()"
          />

          <v-row v-if="isVoucherRedeemed && !isAppointmentScheduled">
            <v-col cols="11" offset="1" align="left">
              <span class="font-weight-bold">Did you schedule your appt?</span>
              <v-btn
                :loading="scheduleAppointmentLoading"
                small
                dense
                tile
                color="white"
                active-class="appointment-scheduled-button-active"
                class="ml-10 mt-n1 appointment-scheduled-button-group"
                data-cy="appointmentScheduledButton"
                @click="handleAppointmentScheduled(new Date())"
              >
                Yes
              </v-btn>
            </v-col>
          </v-row>

          <OfferActionBlock
            header="Use Your Voucher"
            description="Show the voucher when you arrive for your appointment."
            action-text="Show Voucher"
            :action-link="`/coupon/${voucher.id}`"
            :action-static="true"
            :action-disabled="!isAppointmentScheduled"
          />

          <OfferActionBlock
            header="Make Post or Review"
            description="Create your social post or review & upload a screenshot."
            action-text="Start"
            :action-number="2"
            :action-complete="proofs.length > 0"
            :action-disabled="!isAppointmentScheduled"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />

          <OfferActionBlock
            v-if="proofs.length > 0"
            header="Add a Second Post or Review"
            :description="`Love ${business.brand}? Add another post or review & upload a screenshot.`"
            action-text="Start"
            :action-number="3"
            :action-complete="proofs.length > 1"
            :action-sub-text="offer.number_tasks_required > 1 ? '' : 'Optional'"
            :action-disabled="!isAppointmentScheduled"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />
        </v-col>
      </v-row>

      <v-row v-if="isVisitVoucher" class="action-blocks">
        <v-col cols="12">
          <OfferActionBlock
            header="Use Your Voucher"
            description="Be sure to read the limitations on the voucher"
            action-text="Show Voucher"
            :action-number="1"
            :action-link="`/coupon/${voucher.id}`"
            :action-repeatble="true"
            :action-complete="isVoucherRedeemed"
            :action-sub-text="`Expires in ${$options.filters.timeUntil(voucher.date_expires)}`"
          />

          <OfferActionBlock
            header="Make Post or Review"
            description="Create your social post or review & upload a screenshot."
            action-text="Start"
            :action-number="2"
            :action-complete="proofs.length > 0"
            :action-disabled="!isVoucherRedeemed"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />

          <OfferActionBlock
            v-if="proofs.length > 0"
            header="Add a Second Post or Review"
            :description="`Love ${business.brand}? Add another post or review & upload a screenshot.`"
            action-text="Start"
            :action-number="3"
            :action-complete="proofs.length > 1"
            :action-sub-text="offer.number_tasks_required > 1 ? '' : 'Optional'"
            :action-disabled="!isVoucherRedeemed"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />
        </v-col>
      </v-row>

      <v-row v-if="isEventVoucher" class="action-blocks">
        <v-col cols="12">
          <OfferActionBlock
            header="Your Spot is Reserved"
            description="Be sure to read the limitations on the voucher"
            action-text="Show Voucher"
            :action-number="1"
            :action-link="`/coupon/${voucher.id}`"
            :action-repeatble="true"
            :action-complete="isVoucherRedeemed"
            :action-sub-text="`Use on ${getEventDate}`"
          />

          <OfferActionBlock
            header="Make Post or Review"
            description="Create your social post or review & upload a screenshot."
            action-text="Start"
            :action-number="2"
            :action-complete="proofs.length > 0"
            :action-disabled="!isVoucherRedeemed"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />

          <OfferActionBlock
            v-if="proofs.length > 0"
            header="Add a Second Post or Review"
            :description="`Love ${business.brand}? Add another post or review & upload a screenshot.`"
            action-text="Start"
            :action-number="3"
            :action-complete="proofs.length > 1"
            :action-sub-text="offer.number_tasks_required > 1 ? '' : 'Optional'"
            :action-disabled="!isVoucherRedeemed"
            @offer-action-block-link-clicked="handleShowTaskListModal()"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="mt-3">
          <h4 class="text-bold mx-3">Do {{ offer.number_tasks_required }} of the following actions after you use your offer:</h4>
        </v-col>
      </v-row>

      <v-row class="mx-2 mb-5">
        <v-col cols="12">
          <OfferTaskList :task-types="offer.tasks" />
        </v-col>
      </v-row>

      <v-row class="mx-2 mb-5">
        <v-col cols="12">
          <ShareOffer :business-brand="business.brand" :offer-summary="offer.offer_summary" :offer-short-url-id="offer.short_url_id" />
        </v-col>
      </v-row>

      <v-row v-if="isVoucherRedeemed && proofs.length === 0" justify="center" class="mb-5">
        <v-col cols="11">
          <offer-report-problem class="text-center" :voucher-id="voucher.id" />
        </v-col>
      </v-row>

      <v-footer app :fixed="true" :elevation="6" class="white">
        <v-row class="py-3">
          <v-col cols="12">
            <v-btn v-if="isCallVoucher && !isAppointmentScheduled" block rounded color="primary" @click="showScheduleAppointmentModal = true">
              Schedule Appointment
            </v-btn>
            <v-btn v-else-if="isWebVoucher && !isVoucherRedeemed" block rounded color="primary" @click="showWebStartDialog">Proceed to Website & Order</v-btn>
            <v-btn v-else-if="!isVoucherRedeemed" :to="`/coupon/${voucher.id}`" block rounded color="primary">Show Voucher</v-btn>
            <v-btn v-else-if="!isVoucherComplete" block rounded color="primary" @click="handleShowTaskListModal()">Make Post or Review</v-btn>
            <v-btn v-else block rounded color="primary" to="/">See other offers</v-btn>
          </v-col>
        </v-row>
      </v-footer>
    </div>
  </v-container>
</template>

<script>
import Vue from 'vue'

import { ApiService } from '@/services'
import { ReservationCategory } from '@/enums/reservationCategory'

import ShareOffer from '@/components/ShareOffer.vue'
import TaskListModal from '@/components/TaskListModal.vue'
import OfferTaskList from '@/components/OfferTaskList.vue'
import OfferActionBlock from '@/components/OfferActionBlock.vue'
import OfferReportProblem from '@/components/OfferReportProblem.vue'
import ScheduleAppointmentModal from '@/components/ScheduleAppointmentModal.vue'
import dayjs from 'dayjs'

export default Vue.extend({
  name: 'Voucher',

  metaInfo() {
    return {
      title: `${this.offer.offer_summary} | ${this.business.brand}`,
    }
  },

  components: {
    ShareOffer,
    TaskListModal,
    OfferTaskList,
    OfferReportProblem,
    OfferActionBlock,
    ScheduleAppointmentModal,
  },

  beforeRouteUpdate(to, from, next) {
    this.showTaskListModal = to.hash === '#taskList'

    next()
  },

  data: () => ({
    loading: true,
    voucher: null,
    selectedEventDate: null,
    offer: {},
    address: {},
    business: {},
    code: {},
    proofs: [],
    scheduleAppointmentLoading: false,
    showScheduleAppointmentModal: false,
    webStartDialog: false,
    codeDialog: false,
    showTaskListModal: false,
  }),

  computed: {
    getShareTitle: function () {
      return `Check out this offer from ${this.business.brand} - ${this.offer.offer_summary}`
    },
    getShareUrl: function () {
      return `${process.env.VUE_APP_SHORT_URL}${this.offer.short_url_id}`
    },
    getVoucherId: function () {
      return this.$router.currentRoute.params.voucherId
    },
    isVoucherRedeemed: function () {
      return this.voucher.date_redeemed !== null
    },
    isVoucherComplete: function () {
      return this.voucher.voucher_status === 'COMPLETED'
    },
    isAppointmentScheduled: function () {
      return this.voucher.date_appointment_scheduled !== null
    },
    isWebVoucher: function () {
      return this.offer.offer_order_flow_type === 'URL'
    },
    isCallVoucher: function () {
      return this.offer.offer_order_flow_type === 'CALL'
    },
    isVisitVoucher: function () {
      return this.offer.offer_order_flow_type === 'VISIT'
    },
    isEventVoucher: function () {
      return this.offer.offer_order_flow_type === 'EVENT'
    },
    getEventDate: function () {
      return `${dayjs(this.selectedEventDate.event_date).format('ddd MMM D')}`
    },
    getEventDateFull: function () {
      return `${dayjs(this.selectedEventDate.event_date).format('ddd MMM D')} - ${this.selectedEventDate.start_time} - ${this.selectedEventDate.end_time}`
    },
    daysToEvent: function () {
      if (!this.selectedEventDate) return null
      return dayjs(this.selectedEventDate.event_date).startOf('day').diff(dayjs().startOf('day'), 'day')
    },
    daysLeftDisplay: function () {
      return this.daysToEvent ? 'in ' + this.daysToEvent + ' days' : 'today'
    },
    hasWebCode: function () {
      return this.code || this.offer.coupon_code
    },
    webCode: function () {
      if (!this.hasWebCode) return ''
      if (this.code) return this.code.code_val
      return this.offer.coupon_code
    },
  },

  mounted: function () {
    this.showTaskListModal = this.$router.currentRoute.hash === '#taskList'

    ApiService.get(`voucherWorkflow/${this.getVoucherId}`)
      .then((response) => {
        if (!response.data.voucher && response.data.offer.offer_status !== 'ACTIVE') {
          this.$router.replace({ path: '/' })
          this.$toast.warning('Whoops! That offer is no longer available.')
          return
        }

        this.loading = false
        this.voucher = response.data.voucher
        this.offer = response.data.offer
        this.address = response.data.address
        this.business = response.data.business
        this.proofs = response.data.proofs
        this.code = response.data.code
        this.selectedEventDate = response.data.selected_event_date
      })
      .catch(() => {
        this.$router.replace({ path: `/user/offers/${ReservationCategory.INCOMPLETE}` })
        this.$toast.warning('Whoops! There was a problem loading your offer. Please try again.')
      })
  },

  methods: {
    hourStringToAP(s) {
      if (!s || !s.split(':').length === 2) return null
      const hours = Number.parseInt(s.split(':')[0])
      const minutes = s.split(':')[1]
      const adjustedHours = hours % 12
      const prettyHours = adjustedHours === 0 ? 12 : adjustedHours
      const retVal = hours < 12 ? `${prettyHours}:${minutes}a` : `${prettyHours}:${minutes}p`
      return retVal
    },
    handleShowTaskListModal: function () {
      this.$router.push({ path: `/voucher/${this.getVoucherId}#taskList` })
    },
    showWebStartDialog: function () {
      if (this.isVoucherRedeemed) {
        this.codeDialog = true
        return
      }
      this.webStartDialog = true
    },
    copyCode: function () {
      let textToCopy = document.getElementById('promoCode')
      textToCopy.select()
      document.execCommand('copy')

      this.$toast(`Copied ${document.getSelection()} to clipboard`)
      this.codeDialog = false
    },
    redeemWebOffer: function () {
      ApiService.put(`voucherWorkflow/markAsUsed/${this.voucher.id}`, {}).then((response) => {
        this.voucher = response.data.voucher
        if (response.data.code) {
          this.code = response.data.code
        }

        this.webStartDialog = false
        this.codeDialog = true

        this.$store.dispatch('reservations/fetchReservationSummary')
      })
    },
    scheduleVoucher: function () {
      this.showScheduleAppointmentModal = true

      if (this.isVoucherRedeemed) return

      ApiService.put(`voucherWorkflow/markAsUsed/${this.voucher.id}`, {})
        .then((response) => {
          this.voucher = response.data.voucher
          this.code = response.data.code

          this.$store.dispatch('reservations/fetchReservationSummary')
        })
        .catch(() => {
          this.showScheduleAppointmentModal = false

          this.$toast.error('Whoops! There was a problem. Please try again.')
        })
    },
    handleAppointmentScheduled(dateAppointmentScheduled) {
      this.scheduleAppointmentLoading = true

      ApiService.put(`voucherWorkflow/dateAppointmentScheduled/${this.voucher.id}`, { dateAppointmentScheduled })
        .then((response) => {
          if (response.data.error) {
            throw response.data.error
          }

          this.voucher.date_appointment_scheduled = dateAppointmentScheduled
        })
        .catch(() => {
          this.scheduleAppointmentLoading = false

          this.$toast.error('Whoops! There was a problem. Please try again.')
        })
    },
  },
})
</script>

<style scoped>
.header {
  font-size: 25px;
  line-height: 1.3em;
}

.action-blocks {
  padding-top: 25px;
  padding-bottom: 25px;
  background: linear-gradient(#f8fcff, #fff);
}

.appointment-scheduled-button-active {
  font-weight: bold;
  color: white !important;
  background-color: #ed1e56 !important;
}

.appointment-scheduled-button-group {
  border: 1px solid #ed1e56 !important;
}

.brand-name-logo-replacement {
  font-size: 24px;
  font-weight: 600;
}

.rounded-red {
  border-radius: 25px 25px 25px 25px;
  background-color: #ed1e563d;
  height: 50px;
  text-align: center;
  font-size: 26px;
  font-weight: 700;
}

.rounded-corners {
  border-radius: 16px 16px 16px 16px;
}

.big-title {
  font-size: 28px;
  font-weight: bolder;
}

.offer-complete-circle {
  border-radius: 50%;
  width: 71px;
  height: 71px;
  padding: 14px;
  margin-top: -15px;
  margin-bottom: -10px;
  background: #fff;
  text-align: center;
  border: 1px solid #009200;
  color: #009200;
}

.promo-code {
  outline: none;
}
</style>
